import dayjs, { Dayjs } from 'dayjs';
import { DEFAULT_INTERVAL } from 'shared/time';
import { BusinessDayType, TimeConfigType, WorkTimeType } from 'shared/types';
import getNearestBusinessDay from 'utils/datetime/schedule/getNearestBusinessDay';
import getPrevActiveBusinessDay from 'utils/datetime/schedule/getPrevActiveBusinessDay';

import { getDateFromWorkTime } from '../helpers';
import getTimeOptions from '../options/orders/getTimeOptions';

import getNearestPreOrderBusinessDay from './getNearestPreOrderBusinessDay';

type Props = {
	workTimeAll?: WorkTimeType[];
	timeConfig?: TimeConfigType;
	schedules: WorkTimeType[][];
};

/**
 * Get business periods schedule for location WorkTime.
 * @visualization https://www.figma.com/file/SmWuMuCtb9Zb1a3hfqGCh9/Timing-Refactor?type=whiteboard&node-id=0%3A1&t=r2XKTI2c2N8XYYBV-1
 * */
const getBusinessPeriodsSchedule = ({
	workTimeAll = [],
	timeConfig = {
		delay: 0,
		allowTime: true,
		allowASAP: true,
		interval: DEFAULT_INTERVAL,
	},
	schedules,
}: Props) => {
	const { delay, minPreOrdersDayCount, maxPreOrdersDayCount = 7, allowASAP } = timeConfig;
	const nearestPreOrderDate = dayjs(
		getNearestPreOrderBusinessDay({
			workTimeAll,
			delay,
			minPreOrdersDayCount,
		})
	);
	const nearestDate = dayjs(
		getNearestBusinessDay({
			workTimeAll,
			delay,
		})
	);
	const businessDays: BusinessDayType[] = [];
	let maxLoopSize = maxPreOrdersDayCount * 7;

	if (!workTimeAll?.length) return [];
	let startCountDate = allowASAP ? nearestDate : nearestPreOrderDate;

	const prevBusinessSchedule = getPrevActiveBusinessDay({
		date: startCountDate,
		workTimeAll: workTimeAll ?? [],
		delay,
	});
	const isDateStillInPrevBusinessDay = startCountDate.isBefore(
		prevBusinessSchedule.dateWorkTime.tillDelayed
	);

	const maxDaysSize = maxPreOrdersDayCount + (minPreOrdersDayCount ?? 0);

	const firstTimeForSchedule = startCountDate;
	startCountDate = isDateStillInPrevBusinessDay ? prevBusinessSchedule.day : startCountDate;

	for (let i = 0; businessDays.length < maxDaysSize; i++) {
		let prev =
			businessDays.length > 0
				? businessDays[businessDays.length - 1]?.period.till
				: getPrevActiveBusinessDay({
						date: startCountDate,
						workTimeAll,
						delay,
				  }).dateWorkTime.tillDelayed;

		const current =
			i === 0 && startCountDate.isToday()
				? startCountDate
				: startCountDate.add(i, 'day').startOf('day');
		const currentSchedule = workTimeAll.find((d) => d.dayOfWeek === current.day());

		if (currentSchedule?.active) {
			const dayInSchedule = getDateFromWorkTime({
				workTime: currentSchedule,
				delay: timeConfig?.delay,
				date: current.toDate(),
			});

			const timeOptions = _getScheduleIntersections({
				schedules,
				workTimeAll,
				date: current.toDate(),
				timeConfig,
				ownFirstTime:
					firstTimeForSchedule.isAfter(current) && !firstTimeForSchedule.isSame(current, 'date')
						? firstTimeForSchedule.toDate()
						: undefined,
			});

			if (i > maxPreOrdersDayCount * 7) break;

			businessDays.push({
				active: timeOptions.length > 0,
				dayOfWeek: current.day(),
				date: current.format('YYYY-MM-DD'),
				isPreOrder: _getIsPreOrderDay({ current, nearestPreOrderDate, prevBusinessSchedule }),
				period: {
					from: prev,
					till: dayInSchedule.tillDelayed,
				},
				timeOptions: timeOptions,
				workingHours: {
					from: dayInSchedule.fromDelayed,
					till: dayInSchedule.tillDelayed,
				},
			});
		}

		if (i >= maxLoopSize) break;
	}

	return businessDays;
};

/**
 * Get intersections based on section schedules for given dates.
 * 1. Get available intersection for prev limited menu schedule (day)
 * 2. Get each limited menu possible intersections with delay.
 * 3. Combine those schedule into new array
 * 4. If there is no intersections at that level, break loop, there is can't be intersections
 * 5. Filter given date intersections by checking each item is included in schedule array.
 * 6. Check if there is only one item in array and schedules are more than 1, return empty array
 *    e.g. one section end at 12:00, other one start at 12:00, they shouldn't intersect
 * @return an string[] of intersections available for business period
 * */
const _getScheduleIntersections = ({
	schedules,
	timeConfig,
	workTimeAll,
	date,
	ownFirstTime,
}: {
	workTimeAll: WorkTimeType[];
	schedules: WorkTimeType[][];
	timeConfig: TimeConfigType;
	date: Date;
	ownFirstTime?: Date;
}) => {
	let intersections: string[] = getTimeOptions({
		timeConfig,
		workTimeAll,
		date,
		ownFirstTime,
	}).map((i) => i.value);

	const prevDate = dayjs(date).add(-1, 'day').toDate();

	for (const schedule of schedules) {
		const prevScheduleOptions = _getOptionsForSchedule({
			date: prevDate,
			workTimeAll: schedule,
			timeConfig,
			intersections,
		});
		const currentScheduleOptions = _getOptionsForSchedule({
			date,
			workTimeAll: schedule,
			timeConfig,
			intersections,
		});

		intersections = [...prevScheduleOptions, ...currentScheduleOptions];

		// if in one of iterations there is no options, then there is no intersections for all.
		if (intersections.length === 0) {
			intersections = [];
			break;
		}

		// this is for cases when sections intersect at start/end.
		// e.g. one section end at 12:00, other one start at 12:00,
		// they shouldn't be intersected (like so - end: 11:59:59 -> start: 12:00:00).
		if (intersections.length === 1) {
			intersections = [];
		}
	}

	return intersections;
};

/**
 * Get time options for given date and schedule by using getTimeOptions method.
 * */
const _getOptionsForSchedule = ({
	date,
	workTimeAll,
	timeConfig,
	intersections,
}: {
	date: Date;
	workTimeAll: WorkTimeType[];
	timeConfig: TimeConfigType;
	intersections: string[];
}) => {
	const workTime = workTimeAll.find((d) => d.dayOfWeek === date.getDay());

	if (!workTime?.active) return [];

	const dateWorkTime = getDateFromWorkTime({
		date,
		workTime: workTime,
		delay: timeConfig.delay,
	});

	const timeOptions = getTimeOptions({
		timeConfig,
		workTimeAll,
		date,
		ownDateFromWorkTime: dateWorkTime,
	}).map((i) => i.value);

	const filtered = intersections.filter((d) => timeOptions.includes(d));

	if (filtered.length === 1) return [];

	// this is for cases when sections intersect at start/end.
	// e.g. one section end at 12:00, other one start at 12:00,
	// they shouldn't be intersected (like so - end: 11:59:59 -> start: 12:00:00).
	return filtered;
};

/**
 * Get if day is business day have preOrder.
 * Check if previous business schedule extends to current day, and it's same date as nearest preOrder one.
 * Check if current is after/same as nearest possible pre-order day.
 * */
const _getIsPreOrderDay = ({
	nearestPreOrderDate,
	current,
	prevBusinessSchedule,
}: {
	current: Dayjs;
	nearestPreOrderDate: Dayjs;
	prevBusinessSchedule: {
		day: Dayjs;
		workTime: WorkTimeType;
		dateWorkTime: {
			from: Date;
			till: Date;
		};
	};
}) => {
	if (
		current.isSame(prevBusinessSchedule.day, 'date') &&
		nearestPreOrderDate.isSame(prevBusinessSchedule.dateWorkTime.till, 'date')
	)
		return true;

	return current.isAfter(nearestPreOrderDate) || current.isSame(nearestPreOrderDate, 'date');
};

export default getBusinessPeriodsSchedule;
